/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-param-reassign */
import React, { useEffect, useCallback, useContext, useState } from 'react';
import { connect } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Spin, Input, Divider } from 'antd';
import _ from 'lodash';
import ThemeSelect from '../ThemeSelect/ThemeSelect';
import AddressSearch from '../../../NatCatProfile/components/AddressSearch/AddressSearch';
import QuickList from '../QuickList/QuickList';
import TailoredList from '../TailoredList/TailoredList';
import UnderwriterList from '../UnderwriterList/UnderwriterList';
import { fetchInsuranceClasses } from '../../../Document/DocumentActions';
import { fetchHazardIndex, fetchSignificantExposures, fetchRiskChecklist } from '../../../Industry/IndustryActions';
import { setSections, updateReportCustomizations, resetHazardIndexChartDataUrl } from '../../ReportActions';
import {
  hazardIndexCustomizationSelector,
  underwriterHazardIndexCustomizationSelector,
  sectionsSelector,
  quickReportSectionsSelector,
  loadingCustomizationsSelector,
} from '../../ReportSelectors';
import { industryIdSelector, indicativeRateSelector } from '../../../Industry/IndustrySelectors';
import { countryCodeSelector } from '../../../Account/AccountSelectors';
import {
  IconHazardIndex,
  IconSignificantExposures,
  IconRiskChecklist,
  IconNaturalDisasters,
} from '../../../../assets/icons/IndustryFeature';
import { TypeContext } from '../../ReportContext';
import './CheckList.scss';
import appThemes from '../../../../shared/style/themes/themes';
import { appThemeSelector } from '../../../../components/App/AppSelectors';

const getChildrenIds = (item) => item?.children?.map((child) => child.id) || [];
const getRiskChecklistChildrenIds = (item) => item?.children?.map((child) => child.fallbackId || child.id) || [];
const getSectionChildrenIds = (sectionId, sections) =>
  sectionId === 'report-risk-checklist'
    ? getRiskChecklistChildrenIds(sections?.[sectionId])
    : getChildrenIds(sections?.[sectionId]);

const normalizeRiskChecklistCheckedIds = (checkedList, quickReportSections) => {
  const categoryMap = _.keyBy(quickReportSections?.['report-risk-checklist']?.children || [], 'id');
  return (checkedList || []).map((id) => `${categoryMap[id]?.fallbackId || id}`);
};

const CheckList = ({
  industryId,
  country,
  loading,
  loadingIndustry,
  quickReportSections,
  indicativeRate,
  sections,
  themes = [],
  selectedTheme,
  setTheme,
  fetchInsuranceClasses,
  fetchHazardIndex,
  fetchSignificantExposures,
  fetchRiskChecklist,
  setSections,
  customization,
  underwriterCustomization,
  updateReportCustomizations,
  resetHazardIndexChartDataUrl,
  loadingCustomizations,
  underwriter,
  natcatProfiles,
  appTheme,
}) => {
  const type = useContext(TypeContext);
  const [hideIndicativeRate, setHideIndicativeRate] = useState(true);

  const CHECKLIST = [
    {
      id: 'user-profile',
      title: underwriter ? 'Underwriter Details' : 'Meet Your Advisor',
      defaultChecked: true,
      required: appTheme !== appThemes.iprofile,
      onEdit: '/account/user-profile',
    },
    {
      id: 'company-profile',
      title: 'Company Information',
      required: !underwriter,
      defaultChecked: true,
      onEdit: '/account/company-information',
      hide: underwriter,
    },
    {
      id: 'introduction-iprofilerisk',
      title: 'Introduction to iProfileRisk',
      required: true,
      hide: appTheme !== appThemes.iprofile,
    },
    { id: 'about', title: 'About LMI Group', required: true, hide: type === 'Natural Catastrophe Report' },
    { id: 'purpose', title: 'Purpose of this Report', required: true, hide: type === 'Natural Catastrophe Report' },
    {
      id: 'under-insurance',
      title: 'Under Insurance',
      defaultChecked: !underwriter,
      hide: type === 'Natural Catastrophe Report' || country === 'nz' || underwriter,
    },
    {
      id: 'report-hazard-assessment',
      title: 'Hazard Assessment',
      icon: IconHazardIndex,
      required: true,
      hide: type === 'Natural Catastrophe Report',
    },
    {
      id: 'insurable-gross-profit',
      title: 'Indicative Rate of Insurable Gross Profit',
      defaultChecked: true,
      skipSequencing: true,
      hide: hideIndicativeRate || type === 'Natural Catastrophe Report',
    },
    {
      id: 'report-significant-exposures',
      title: 'Significant Exposures',
      icon: IconSignificantExposures,
      hide: type === 'Natural Catastrophe Report',
    },
    {
      id: 'report-natural-catastrophe',
      icon: IconNaturalDisasters,
      title: 'Our Nat Cat source',
      hide: appTheme !== appThemes.iprofile,
      defaultChecked: !!natcatProfiles,
      disabled: !natcatProfiles,
    },
    {
      id: 'report-risk-checklist',
      title: 'Risk Checklist',
      icon: IconRiskChecklist,
      hide: underwriter || type === 'Natural Catastrophe Report',
    },
  ].filter((item) => !item.hide);

  const location = useLocation();

  const saveReportCustomizationName = useCallback(
    _.debounce(async (name) => {
      const lastSegment = location.pathname.substring(location.pathname.lastIndexOf('/') + 1);
      if (lastSegment.toLowerCase() === 'tailored-report') {
        if (!customization.isPristine)
          await updateReportCustomizations(industryId, customization.id, name, customization.customizations);
      }

      if (lastSegment.toLowerCase() === 'underwriter-report') {
        if (!underwriterCustomization?.isPristine)
          await updateReportCustomizations(
            industryId,
            underwriterCustomization.id,
            name,
            underwriterCustomization.customizations
          );
      }
    }, 500),
    [customization, underwriterCustomization]
  );

  useEffect(() => {
    if (indicativeRate && !loadingIndustry) {
      if (indicativeRate.length > 0) setHideIndicativeRate(false);
      else setHideIndicativeRate(true);
    }
  }, [loadingIndustry, indicativeRate]);

  useEffect(() => {
    fetchInsuranceClasses('au');
  }, [fetchInsuranceClasses]);

  useEffect(() => {
    if (industryId && country) {
      fetchHazardIndex(industryId, country);
      fetchSignificantExposures(industryId, country);
    }
  }, [industryId, country, fetchHazardIndex, fetchSignificantExposures]);

  useEffect(() => {
    const initialSections = _.reduce(
      CHECKLIST,
      (result, item) => {
        if (item.required || item.defaultChecked) {
          result[item.id] = {
            checked: true,
            skipSequencing: item.skipSequencing ? item.skipSequencing : false,
            children: getSectionChildrenIds(item.id, quickReportSections),
          };
        } else {
          result[item.id] = {
            checked: false,
            skipSequencing: item.skipSequencing ? item.skipSequencing : false,
            children: [],
          };
        }
        return result;
      },
      {}
    );
    setSections({ cover: sections?.cover, ...initialSections });
  }, [quickReportSections, fetchRiskChecklist, setSections, industryId]);

  useEffect(() => {
    if (industryId) {
      const categoryIds = getSectionChildrenIds('report-risk-checklist', quickReportSections);
      fetchRiskChecklist(industryId, { categories: categoryIds, type: 'Key' });
    }
  }, [industryId]);

  useEffect(() => {
    if (industryId && type !== 'Natural Catastrophe Report') {
      const categoryIds = getSectionChildrenIds('report-risk-checklist', quickReportSections);
      fetchRiskChecklist(industryId, { categories: categoryIds, type: 'Key' });
    }
  }, [industryId]);

  if (!sections || loading) {
    return <Spin />;
  }

  const list = CHECKLIST.map((item) => {
    return {
      ...item,
      anchor: item.id,
      checked: sections[item.id] ? sections[item.id].checked : false,
      children: quickReportSections[item.id] ? quickReportSections[item.id].children : [],
      checkedChildren: sections[item.id] ? sections[item.id].children : [],
    };
  });

  const onCheck = (checked, value) => {
    setSections({
      ...sections,
      [value]: {
        ...sections[value],
        checked,
        children: checked ? getSectionChildrenIds(value, quickReportSections) : [],
      },
    });
  };

  const onGroupCheck = (checkedList, parentId, effectParent = true) => {
    const normalizedCheckedList =
      parentId === 'report-risk-checklist'
        ? normalizeRiskChecklistCheckedIds(checkedList, quickReportSections)
        : checkedList;
    let values = { checked: normalizedCheckedList?.length > 0, children: normalizedCheckedList };
    if (!effectParent) {
      values = { checked: sections[parentId].checked, children: normalizedCheckedList };
    }
    setSections({ ...sections, [parentId]: values });
  };

  const onNameChange = (e) => {
    setSections({ ...sections, cover: { insuredBusinessName: e.target.value } });
    saveReportCustomizationName(e.target.value);
  };

  return (
    <>
      <h3 className="drawer-content-title">Theme</h3>
      <ThemeSelect
        themes={themes}
        selectedTheme={selectedTheme}
        onThemeSelect={setTheme}
        hideCustomThemeButton={appTheme === appThemes.iprofile}
      />
      <Divider dashed className="drawer-content-divider" />

      <h3 className="drawer-content-title">Insured Business Name</h3>
      <Input
        size="large"
        placeholder="Insured Business Name"
        onChange={onNameChange}
        defaultValue={customization?.name || underwriterCustomization?.name}
      />
      <Divider dashed className="drawer-content-divider" />
      {appTheme === appThemes.iprofile && (
        <AddressSearch showText onAfterChange={() => onCheck(true, 'report-natural-catastrophe')} />
      )}

      <h3 className="drawer-content-title">Contents</h3>
      {(type === 'Quick Report' || type === 'Natural Catastrophe Report') && (
        <QuickList list={list} onCheck={onCheck} onGroupCheck={onGroupCheck} />
      )}
      {type === 'Tailored Report' && <TailoredList list={list} onCheck={onCheck} onGroupCheck={onGroupCheck} />}
      {underwriter && (
        <UnderwriterList
          list={list}
          onCheck={onCheck}
          onGroupCheck={onGroupCheck}
          loading={loading}
          sections={sections}
          industryId={industryId}
          customization={underwriterCustomization}
          updateReportCustomizations={updateReportCustomizations}
          loadingCustomizations={loadingCustomizations}
          resetHazardIndexChartDataUrl={resetHazardIndexChartDataUrl}
        />
      )}
    </>
  );
};

const mapStateToProps = (state) => ({
  industryId: industryIdSelector(state),
  country: countryCodeSelector(state),
  loadingIndustry: state.industry.loadingIndustry,
  loadingCustomizations: loadingCustomizationsSelector(state),
  loading:
    state.industry.loadingIndustry ||
    state.industry.loadingHazardIndex ||
    state.industry.loadingSignificantExposures ||
    state.document.loadingInsuranceClasses,
  sections: sectionsSelector(state),
  quickReportSections: quickReportSectionsSelector(state),
  customization: hazardIndexCustomizationSelector(state),
  underwriterCustomization: underwriterHazardIndexCustomizationSelector(state),
  indicativeRate: indicativeRateSelector(state),
  natcatProfiles: state.natcat.natcatProfiles,
  riskChecklist: state.industry.riskChecklist,
  appTheme: appThemeSelector(state),
});

const mapDispatchToProps = {
  fetchInsuranceClasses,
  fetchHazardIndex,
  fetchSignificantExposures,
  fetchRiskChecklist,
  setSections,
  updateReportCustomizations,
  resetHazardIndexChartDataUrl,
};

export default connect(mapStateToProps, mapDispatchToProps)(CheckList);
